import React from "react";
import { useTranslation } from "react-i18next";
import "./TelealarmeISSProofs.css";

/**
 * Téléalarme ISS — 06 / Preuves & Confiance
 *
 * Présentation de la démarche de justification technique.
 * Les rubriques sont des points à documenter selon l'installation :
 * elles ne valent PAS certification, essai réussi ou promesse de performance.
 *
 * Illustration : image matériel déjà référencée par la section Équipements.
 * Le fichier source /images/Tele_part.png reste dans le projet existant.
 */
const CONTENT = {
  fr: {
    eyebrow: "CONFIANCE INDUSTRIELLE",
    titleFirst: "Des équipements identifiables.",
    titleSecond: "Des engagements vérifiables.",
    intro: "La confiance repose sur des documents et des essais, pas sur des slogans.",
    imageAlt:
      "Illustration des équipements de téléalarme ISS ; modèles et versions à confirmer lors de l'étude de l'installation.",
    imageCaption: "Visuel produit — références et versions à confirmer",
    criteria: [
      {
        id: "references",
        title: "Fiches et références exactes",
        description: "Identifier chaque équipement, son modèle et sa version."
      },
      {
        id: "documentation",
        title: "Documentation technique à jour",
        description: "Vérifier les notices applicables, leur version et leur périmètre."
      },
      {
        id: "tests",
        title: "Essais de communication documentés",
        description: "Préciser les essais réalisés et les résultats confirmés, lorsqu'ils sont disponibles."
      },
      {
        id: "conditions",
        title: "Conditions de réseau et de secours",
        description: "Examiner la couverture, l'alimentation et les dispositifs de secours requis."
      }
    ],
    note:
      "Ces critères guident l'étude de chaque configuration. Aucun essai, agrément ou niveau de performance n'est présenté ici comme déjà établi."
  },
  en: {
    eyebrow: "INDUSTRIAL CONFIDENCE",
    titleFirst: "Identifiable equipment.",
    titleSecond: "Verifiable commitments.",
    intro: "Confidence comes from documentation and testing, not slogans.",
    imageAlt:
      "Illustration of ISS emergency call equipment; model numbers and versions must be confirmed for the installation.",
    imageCaption: "Product visual — models and versions to be confirmed",
    criteria: [
      {
        id: "references",
        title: "Exact product references",
        description: "Identify each device, its model and its version."
      },
      {
        id: "documentation",
        title: "Up-to-date technical documentation",
        description: "Check applicable manuals, versions and operating scope."
      },
      {
        id: "tests",
        title: "Documented communication tests",
        description: "Describe tests carried out and confirmed results, where available."
      },
      {
        id: "conditions",
        title: "Network and backup conditions",
        description: "Review coverage, power supply and required backup arrangements."
      }
    ],
    note:
      "These criteria inform the assessment of each configuration. No test, approval or performance level is claimed here as already established."
  }
};

function CriteriaSymbol() {
  return (
    <svg
      className="iss-proofs__criteria-icon"
      viewBox="0 0 28 28"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M14 2.75 25.25 14 14 25.25 2.75 14 14 2.75Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M14 7v14M7 14h14"
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.4"
      />
    </svg>
  );
}

export default function TelealarmeISSProofs() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const content = CONTENT[lang] || CONTENT.fr;

  return (
    <section
      id="iss-proofs"
      className="iss-proofs"
      aria-labelledby="iss-proofs-title"
    >
      <div className="iss-proofs__container">
        <header className="iss-proofs__header">
          <p className="iss-proofs__eyebrow">{content.eyebrow}</p>
          <h2 id="iss-proofs-title" className="iss-proofs__title">
            <span>{content.titleFirst}</span>
            <span>{content.titleSecond}</span>
          </h2>
          <p className="iss-proofs__lead">{content.intro}</p>
        </header>

        <div className="iss-proofs__grid">
          <figure className="iss-proofs__figure">
            <div className="iss-proofs__image-wrap">
              <img
                className="iss-proofs__image"
                src="/images/Tele_part.png"
                alt={content.imageAlt}
                loading="lazy"
                decoding="async"
                width="900"
                height="550"
              />
            </div>
            <figcaption className="iss-proofs__image-caption">
              {content.imageCaption}
            </figcaption>
          </figure>

          <div className="iss-proofs__right">
            <ul className="iss-proofs__criteria">
              {content.criteria.map((item) => (
                <li className="iss-proofs__criterion" key={item.id}>
                  <CriteriaSymbol />
                  <div className="iss-proofs__criterion-copy">
                    <h3 className="iss-proofs__criterion-title">{item.title}</h3>
                    <p className="iss-proofs__criterion-description">
                      {item.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="iss-proofs__disclaimer">{content.note}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
